// Castintech dashboard — Workflow Trace view (Faz 38 PR-A).
//
// Audit-C: "Ekip çalışıyor ama ne çalıştığını ben anlamıyorum." Faz 37 PR-A
// daily digest günlük özet veriyor; bu panel anlık tek-komut zincirini
// görselleştirir:
//   Telegram komut → GM-Gating → Nexus → Hedef Ajan → Handoff'lar → Toparlama.
//
// 2 endpoint:
//   GET /castintech-api/workflow-trace/recent  → sol panel listesi
//   GET /castintech-api/workflow-trace/<id>    → seçilen detay tree
//
// SVG yerine semantik HTML hiyerarşi (ekran okuyucu + kopyala dostu):
//   <ol class="trace-steps"><li class="trace-step"> ... </li></ol>
// ile her step indented, agent name + action + duration + status icon.

import { apiFetch } from '../api.js';
import { escHtml, escAttr, ago, $ } from '../utils.js';

interface RecentTrace {
  session_id: string;
  issue_id: string;
  chat_id: string;
  agent_name: string;
  created_at: string;
  step_count: number;
  last_action: string | null;
  outcome: string;
  total_duration_ms: number;
}

interface TraceStep {
  order: number;
  agent: string;
  action: string;
  label: string;
  from: string | null;
  to: string | null;
  issue_id: string;
  sub_issue_id: string | null;
  actor: string | null;
  comment_excerpt: string | null;
  timestamp: string;
  raw_message: string | null;
  duration_ms: number;
  terminal: boolean;
}

interface TraceDetail {
  session_id: string;
  issue_id: string;
  issue_title: string | null;
  issue_state: string | null;
  initiated_at: string;
  initiator: string;
  chat_id: string;
  original_command: string | null;
  steps: TraceStep[];
  total_duration_ms: number;
  outcome: string;
  step_count: number;
}

let recentTraces: RecentTrace[] = [];
let selectedTrace: TraceDetail | null = null;
let selectedSessionId: string | null = null;
let searchQuery = '';
let _delegationBound = false;

const OUTCOME_BADGE: Record<string, string> = {
  completed: 'green',
  'in-progress': 'topaz',
  failed: 'red',
  blocked: 'red',
  pending: 'topaz',
  resolved: 'green',
  unknown: 'muted',
};

const OUTCOME_ICON: Record<string, string> = {
  completed: '✅',
  'in-progress': '⏳',
  failed: '❌',
  blocked: '🚫',
  pending: '⏳',
  resolved: '✅',
  unknown: '❔',
};

function fmtDuration(ms: number): string {
  if (!ms || !isFinite(ms) || ms < 0) return '0ms';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
  return `${Math.floor(ms / 3_600_000)}h ${Math.floor((ms % 3_600_000) / 60_000)}m`;
}

export async function loadWorkflowTrace(): Promise<void> {
  try {
    const res = await apiFetch('/castintech-api/workflow-trace/recent?limit=20').catch(() => null);
    if (res && res.ok) {
      const j = (await res.json()) as { traces?: RecentTrace[] };
      recentTraces = Array.isArray(j.traces) ? j.traces : [];
    }
    // İlk yüklemede ilk trace'i otomatik aç
    if (!selectedSessionId && recentTraces.length > 0) {
      await selectTrace(recentTraces[0].session_id);
    } else {
      renderWorkflowTrace();
    }
  } catch (e) {
    console.warn('loadWorkflowTrace failed:', (e as Error).message);
    renderWorkflowTrace();
  }
}

async function selectTrace(id: string): Promise<void> {
  if (!id) return;
  selectedSessionId = id;
  try {
    const res = await apiFetch(`/castintech-api/workflow-trace/${encodeURIComponent(id)}`).catch(() => null);
    if (res && res.ok) {
      selectedTrace = (await res.json()) as TraceDetail;
    } else if (res && res.status === 404) {
      selectedTrace = null;
    } else {
      selectedTrace = null;
    }
  } catch {
    selectedTrace = null;
  }
  renderWorkflowTrace();
}

function renderRecentList(): string {
  const filter = searchQuery.toLowerCase().trim();
  const filtered = filter
    ? recentTraces.filter(t =>
        (t.session_id || '').toLowerCase().includes(filter) ||
        (t.issue_id || '').toLowerCase().includes(filter) ||
        (t.chat_id || '').toLowerCase().includes(filter) ||
        (t.agent_name || '').toLowerCase().includes(filter)
      )
    : recentTraces;

  if (!filtered.length) {
    return '<div class="audit-empty">Trace yok.</div>';
  }
  return filtered.map(t => {
    const active = t.session_id === selectedSessionId ? ' active' : '';
    const badge = OUTCOME_BADGE[t.outcome] || 'muted';
    const icon = OUTCOME_ICON[t.outcome] || '❔';
    return `
    <div class="audit-row trace-recent-row${active}" data-action="select-trace" data-session-id="${escAttr(t.session_id)}" style="cursor:pointer;">
      <span class="audit-badge ${escAttr(badge)}" title="${escAttr(t.outcome)}">${icon}</span>
      <div class="audit-body">
        <div class="audit-msg">
          <b>#${escHtml(t.session_id)}</b> · ${escHtml(t.agent_name || '?')}
        </div>
        <div class="audit-meta-line">
          chat=${escHtml(t.chat_id)} · ${t.step_count} adım · ${escHtml(fmtDuration(t.total_duration_ms))}
        </div>
      </div>
      <span class="audit-time" title="${escAttr(t.created_at)}">${ago(t.created_at)} once</span>
    </div>`;
  }).join('');
}

function renderTraceTree(): string {
  if (!selectedTrace) {
    return '<div class="audit-empty">Sol panelden bir trace seç.</div>';
  }
  const t = selectedTrace;
  const badge = OUTCOME_BADGE[t.outcome] || 'muted';
  const icon = OUTCOME_ICON[t.outcome] || '❔';
  const header = `
    <div class="trace-header" style="margin-bottom:12px;">
      <div class="audit-msg" style="font-size:14px;font-weight:600;">
        <span class="audit-badge ${escAttr(badge)}">${icon} ${escHtml(t.outcome)}</span>
        Session #${escHtml(t.session_id)}
        ${t.issue_title ? `· ${escHtml(t.issue_title)}` : ''}
      </div>
      <div class="audit-meta-line" style="margin-top:4px;">
        Initiator: ${escHtml(t.initiator)} ·
        Issue: <code>${escHtml(t.issue_id || '-')}</code> ·
        Toplam: <b>${escHtml(fmtDuration(t.total_duration_ms))}</b> ·
        Adım: ${t.step_count}
      </div>
      ${t.original_command ? `<div class="audit-meta-line" style="margin-top:4px;">Komut: <code>${escHtml(t.original_command)}</code></div>` : ''}
    </div>`;

  if (!t.steps.length) {
    return header + '<div class="audit-empty">Henüz audit step yok.</div>';
  }

  const steps = t.steps.map(s => {
    const stepIcon = s.terminal ? '🏁' : '▸';
    const indent = Math.min(s.order - 1, 5) * 16;
    const fromTo = (s.from || s.to) ? `${escHtml(s.from || '?')} → ${escHtml(s.to || '?')}` : '';
    return `
      <li class="trace-step" style="margin-left:${indent}px;list-style:none;border-left:2px solid var(--border);padding:8px 12px;margin-bottom:6px;background:var(--card-bg,transparent);">
        <div class="audit-msg">
          <span class="audit-badge indigo">${stepIcon} ${s.order}</span>
          <b>${escHtml(s.agent)}</b> · ${escHtml(s.label)}
          ${fromTo ? ` <span class="audit-meta-line" style="display:inline;">(${fromTo})</span>` : ''}
        </div>
        <div class="audit-meta-line">
          ${s.actor ? `actor=${escHtml(s.actor)} · ` : ''}
          duration=<b>${escHtml(fmtDuration(s.duration_ms))}</b> ·
          <span title="${escAttr(s.timestamp)}">${ago(s.timestamp)} once</span>
          ${s.sub_issue_id ? ` · sub=<code>${escHtml(s.sub_issue_id)}</code>` : ''}
        </div>
        ${s.comment_excerpt ? `<div class="audit-meta-line" style="margin-top:4px;font-style:italic;opacity:0.8;">${escHtml(s.comment_excerpt)}</div>` : ''}
      </li>`;
  }).join('');

  return header + `<ol class="trace-steps" style="padding:0;margin:0;">${steps}</ol>`;
}

export function renderWorkflowTrace(): void {
  const root = $('workflow-trace-content');
  const sub = $('workflow-trace-sub');
  if (!root) return;
  if (sub) {
    sub.textContent = `${recentTraces.length} recent trace${selectedTrace ? ' · ' + selectedTrace.step_count + ' adım seçili' : ''}`;
  }
  root.innerHTML = `
    <div class="health-grid" style="grid-template-columns:minmax(280px,360px) 1fr;gap:16px;">
      <div class="health-card">
        <div class="hc-title">Recent Traces</div>
        <div class="hc-body">
          <input type="text" id="workflow-trace-search" placeholder="session_id veya issue_id ara…"
            value="${escAttr(searchQuery)}"
            style="width:100%;padding:6px 10px;margin-bottom:10px;border:1px solid var(--border);border-radius:4px;background:var(--card-bg,#222);color:inherit;font-size:12px;" />
          <div id="workflow-trace-recent-list">${renderRecentList()}</div>
        </div>
      </div>
      <div class="health-card">
        <div class="hc-title">Trace Detay</div>
        <div class="hc-body">${renderTraceTree()}</div>
      </div>
    </div>
  `;
  bindWorkflowTraceDelegation();
}

// Delegated click + input handler — re-render sonrası DOM yenilense de
// listener kaybolmaz çünkü root container'a bağlı.
function bindWorkflowTraceDelegation(): void {
  if (_delegationBound) return;
  const root = $('workflow-trace-content');
  if (!root) return;
  root.addEventListener('click', (e: MouseEvent) => {
    const t = e.target as HTMLElement | null;
    if (!t) return;
    const row = t.closest<HTMLElement>('[data-action="select-trace"]');
    if (row && row.dataset.sessionId) {
      void selectTrace(row.dataset.sessionId);
    }
  });
  root.addEventListener('input', (e: Event) => {
    const t = e.target as HTMLInputElement | null;
    if (t && t.id === 'workflow-trace-search') {
      searchQuery = t.value || '';
      const list = $('workflow-trace-recent-list');
      if (list) list.innerHTML = renderRecentList();
    }
  });
  _delegationBound = true;
}
